import { useCallback, useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-administration';

export type Invitation = components['schemas']['Invitation'];
export type InvitationStatus = components['schemas']['InvitationStatus'];

/** The status badge's tone for each status, shared by the list's rows and the card. */
export const INVITATION_TONE: Record<InvitationStatus, string> = {
  pending: 'info',
  accepted: 'success',
  expired: 'warning',
  withdrawn: '',
};

export type InvitationFilters = {
  readonly q: string;
  readonly status: InvitationStatus | null;
  readonly sort: 'created' | 'expires';
};

export type InvitationsState =
  | { readonly status: 'loading'; readonly items: readonly Invitation[] }
  | {
      readonly status: 'ready';
      readonly items: readonly Invitation[];
      readonly total: number;
      readonly hasMore: boolean;
      readonly loadingMore: boolean;
    }
  | {
      readonly status: 'error';
      readonly items: readonly Invitation[];
      readonly code: unknown;
      readonly httpStatus: number | undefined;
    };

function readPage(
  placeKind: 'tenants' | 'organizations',
  placeId: string,
  { q, status, sort }: InvitationFilters,
  cursor: string | null,
) {
  return apiClient.GET('/v1/administration/{placeKind}/{placeId}/invitations', {
    params: {
      path: { placeKind, placeId },
      query: {
        ...(q !== '' ? { q } : {}),
        ...(status !== null ? { status } : {}),
        sort,
        ...(cursor !== null ? { cursor } : {}),
      },
    },
  });
}

type Page = NonNullable<Awaited<ReturnType<typeof readPage>>['data']>;

function readyState(page: Page, items: readonly Invitation[]): InvitationsState {
  return {
    status: 'ready',
    items,
    total: page.total,
    hasMore: page.nextCursor !== null,
    loadingMore: false,
  };
}

/**
 * Inviti's rows (FR-IN-4): every invitation of the place, pending, accepted, expired and withdrawn,
 * searched by email and sorted by date on the server, with «Mostra altri» through the cursor.
 */
export function useInvitations(
  placeKind: 'tenants' | 'organizations',
  placeId: string,
  filters: InvitationFilters,
): { readonly state: InvitationsState; readonly more: () => void; readonly reload: () => void } {
  const [state, setState] = useState<InvitationsState>({ status: 'loading', items: [] });
  const [cursor, setCursor] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  // Every first page starts a new generation: a «Mostra altri» page asked for an earlier one is
  // dropped when it answers, instead of being appended to a list it no longer belongs to.
  const generation = useRef(0);
  const { q, status, sort } = filters;

  useEffect(() => {
    generation.current += 1;
    let cancelled = false;
    setState({ status: 'loading', items: [] });
    setCursor(null);
    void readPage(placeKind, placeId, { q, status, sort }, null).then(
      ({ data, error, response }) => {
        if (cancelled) {
          return;
        }
        if (data === undefined || !response.ok) {
          setState({ status: 'error', items: [], code: error, httpStatus: response.status });
          return;
        }
        setCursor(data.nextCursor);
        setState(readyState(data, data.items));
      },
      () => {
        if (!cancelled) {
          setState({ status: 'error', items: [], code: null, httpStatus: undefined });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [placeKind, placeId, q, status, sort, attempt]);

  const more = useCallback(() => {
    if (state.status !== 'ready' || cursor === null || state.loadingMore) {
      return;
    }
    const before = state;
    const asked = generation.current;
    setState({ ...before, loadingMore: true });
    void readPage(placeKind, placeId, { q, status, sort }, cursor).then(
      ({ data, error, response }) => {
        if (generation.current !== asked) {
          return;
        }
        if (data === undefined || !response.ok) {
          setState({
            status: 'error',
            items: before.items,
            code: error,
            httpStatus: response.status,
          });
          return;
        }
        setCursor(data.nextCursor);
        setState(readyState(data, [...before.items, ...data.items]));
      },
      () => {
        if (generation.current === asked) {
          setState({ status: 'error', items: before.items, code: null, httpStatus: undefined });
        }
      },
    );
  }, [state, cursor, placeKind, placeId, q, status, sort]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, more, reload };
}
